import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import clientApi from '../../clientApi';
import logo from '../../assets/logo.png';

const statusLabel = { in_progress: 'In Progress', on_hold: 'On Hold', completed: 'Completed' };

const ClientPortal = () => {
  const [jobs, setJobs] = useState(null);
  const [activeJob, setActiveJob] = useState(null);
  const [thread, setThread] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const bottomRef = useRef(null);
  const customer = JSON.parse(localStorage.getItem('cw_client_customer') || 'null');

  const loadJobs = useCallback(async () => {
    try {
      const res = await clientApi.get('/api/client/jobs');
      setJobs(res.data);
      if (res.data.length === 1) setActiveJob(res.data[0].id);
    } catch (err) {
      if (err.response?.status !== 401) setError('Could not load your projects.');
    }
  }, []);

  const loadThread = useCallback(async (jobId) => {
    try {
      const res = await clientApi.get(`/api/client/jobs/${jobId}/messages`);
      setThread(res.data);
    } catch (err) {
      setError('Could not load messages.');
    }
  }, []);

  const loadSchedule = useCallback(async (jobId) => {
    try {
      const res = await clientApi.get(`/api/client/jobs/${jobId}/schedule`);
      setSchedule(res.data.schedule);
    } catch (err) {
      setSchedule([]); // card simply hides
    }
  }, []);

  useEffect(() => { loadJobs(); }, [loadJobs]);
  useEffect(() => { if (activeJob) { loadThread(activeJob); loadSchedule(activeJob); } }, [activeJob, loadThread, loadSchedule]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [thread]);

  const send = async (e) => {
    e.preventDefault();
    if (!message.trim() || sending) return;
    setSending(true);
    try {
      await clientApi.post(`/api/client/jobs/${activeJob}/messages`, { body: message });
      setMessage('');
      loadThread(activeJob);
    } catch (err) {
      setError('Message failed to send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('cw_client_token');
    localStorage.removeItem('cw_client_customer');
    navigate('/client/login');
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--navy)' }}>
      {/* Header */}
      <div style={{ borderBottom: '1px solid rgba(230,190,138,0.15)', padding: '16px 24px', paddingTop: 'calc(16px + env(safe-area-inset-top, 0px))', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <img src={logo} alt="CW" style={{ width: 44, height: 44, borderRadius: 6 }} />
          <div>
            <div style={{ color: 'var(--gold)', fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 500 }}>Crossman & Walsh</div>
            <div style={{ color: 'rgba(230,190,138,0.5)', fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', fontSize: 13 }}>Client Portal</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>{customer?.name}</span>
          <button onClick={logout} style={{ background: 'none', border: '1px solid rgba(230,190,138,0.3)', color: 'rgba(230,190,138,0.8)', padding: '7px 16px', borderRadius: 4, fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', cursor: 'pointer' }}>
            Sign Out
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 820, margin: '0 auto', padding: '28px 20px' }}>
        {error && <div className="alert alert-error">{error}</div>}

        {!jobs ? <div className="loader" /> : jobs.length === 0 ? (
          <div className="card"><div className="card-body"><div className="empty-state">No projects linked to your account yet. If that seems wrong, get in touch with us.</div></div></div>
        ) : (
          <>
            {/* Project picker (only when more than one) */}
            {jobs.length > 1 && (
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
                {jobs.map(j => (
                  <button
                    key={j.id}
                    onClick={() => setActiveJob(j.id)}
                    style={{
                      padding: '10px 18px', borderRadius: 4, cursor: 'pointer', fontSize: 13,
                      background: activeJob === j.id ? 'var(--gold)' : 'rgba(255,255,255,0.05)',
                      color: activeJob === j.id ? 'var(--navy)' : 'rgba(255,255,255,0.7)',
                      border: '1px solid rgba(230,190,138,0.25)'
                    }}
                  >
                    {j.name}
                  </button>
                ))}
              </div>
            )}

            {/* Schedule of works (only shown once we publish one) */}
            {schedule.length > 0 && (
              <div className="card" style={{ marginBottom: 20 }}>
                <div className="card-header"><h2 className="card-title">Schedule of Works</h2></div>
                <div className="card-body">
                  {schedule.map((item, i) => {
                    const statusMeta = {
                      upcoming: { label: 'Upcoming', color: 'rgba(255,255,255,0.45)' },
                      in_progress: { label: 'In Progress', color: 'var(--gold)' },
                      complete: { label: 'Complete', color: '#7dc98f' },
                      delayed: { label: 'Delayed', color: '#e0a04f' },
                    }[item.status] || { label: item.status, color: 'rgba(255,255,255,0.45)' };
                    const fmtD = (d) => d ? new Date(d.substring(0, 10) + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }) : null;
                    const dates = [fmtD(item.start_date), fmtD(item.end_date)].filter(Boolean);
                    return (
                      <div key={item.id} style={{ display: 'flex', gap: 14, paddingBottom: 14, marginBottom: 14, borderBottom: i < schedule.length - 1 ? '1px solid rgba(255,255,255,0.08)' : 'none', alignItems: 'flex-start' }}>
                        <div style={{ minWidth: 28, height: 28, borderRadius: '50%', background: item.status === 'complete' ? 'var(--gold)' : 'rgba(255,255,255,0.06)', border: '1px solid rgba(230,190,138,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: item.status === 'complete' ? 'var(--navy)' : 'rgba(255,255,255,0.6)', fontWeight: 600 }}>
                          {item.status === 'complete' ? '✓' : i + 1}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            <span style={{ color: 'var(--white)', fontSize: 14, fontWeight: 500 }}>{item.title}</span>
                            <span style={{ color: statusMeta.color, fontSize: 11, letterSpacing: 1, textTransform: 'uppercase' }}>{statusMeta.label}</span>
                          </div>
                          <div style={{ color: dates.length > 0 ? 'rgba(230,190,138,0.7)' : 'rgba(255,255,255,0.4)', fontSize: 12, marginTop: 3, fontStyle: dates.length > 0 ? 'normal' : 'italic' }}>
                            {dates.length > 0 ? dates.join(' — ') : 'Dates to be confirmed'}
                          </div>
                          {item.details && (
                            <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, marginTop: 4, whiteSpace: 'pre-wrap' }}>{item.details}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {thread && (
              <div className="card">
                <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h2 className="card-title">{thread.job.name}</h2>
                  <span className={`badge ${thread.job.status === 'completed' ? 'badge-holiday' : thread.job.status === 'on_hold' ? 'badge-pending' : 'badge-approved'}`}>
                    {statusLabel[thread.job.status] || thread.job.status}
                  </span>
                </div>
                <div className="card-body">
                  <div style={{ maxHeight: 420, overflowY: 'auto', marginBottom: 18, paddingRight: 6 }}>
                    {thread.messages.length === 0 && (
                      <div className="empty-state">No messages yet. Say hello — we'll get straight back to you.</div>
                    )}
                    {thread.messages.map(m => {
                      const mine = m.sender_type === 'customer';
                      return (
                        <div key={m.id} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', marginBottom: 12 }}>
                          <div style={{
                            maxWidth: '78%', padding: '10px 14px', borderRadius: 8,
                            background: mine ? 'rgba(230,190,138,0.15)' : 'rgba(255,255,255,0.06)',
                            border: `1px solid ${mine ? 'rgba(230,190,138,0.3)' : 'rgba(255,255,255,0.08)'}`
                          }}>
                            <div style={{ color: 'var(--gold)', fontSize: 11, marginBottom: 4 }}>
                              {mine ? 'You' : (m.staff_name || 'Crossman & Walsh')}
                            </div>
                            <div style={{ color: 'var(--white)', fontSize: 14, whiteSpace: 'pre-wrap' }}>{m.body}</div>
                            <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 10, marginTop: 6 }}>
                              {new Date(m.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={bottomRef} />
                  </div>

                  <form onSubmit={send} style={{ display: 'flex', gap: 10 }}>
                    <input
                      className="form-control"
                      style={{ flex: 1 }}
                      placeholder="Write a message..."
                      value={message}
                      onChange={e => setMessage(e.target.value)}
                    />
                    <button type="submit" className="btn btn-gold" disabled={sending}>
                      {sending ? 'Sending...' : 'Send'}
                    </button>
                  </form>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ClientPortal;
